import { colors, fonts, zIndex } from 'component-library/__styling/settings'
import { calcRem } from 'component-library/__styling/style-functions'
import { gptAdSlotClassname } from '../gpt/GptAdSlot/GptAdSlot'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { themedValue } from 'component-library/__styling/themed-value'
import { CSSObject } from '@emotion/css'
import styled from '@emotion/styled'

export interface VisiblityBreakpoint {
    breakpoint: number
    visible: boolean
    placeholderHeight: number | undefined
}

export type Padding = [number] | [number, number] | [number, number, number, number]
export type AdNoticePosition = 'below-right' | 'top-right' | 'above-center' | 'none'

const getBreakpointVisibilities = (
    visibilityBreakpoints?: VisiblityBreakpoint[]
): CSSObject | {} => {
    if (!visibilityBreakpoints) {
        return {}
    }

    const styles: CSSObject = {}

    visibilityBreakpoints.sort((a, b) => {
        if (a.breakpoint > b.breakpoint) {
            return 1
        }
        if (a.breakpoint < b.breakpoint) {
            return -1
        }
        return 0
    })

    for (const visibilityBreakpoint of visibilityBreakpoints) {
        const breakpointKey = `@media (min-width: ${visibilityBreakpoint.breakpoint}px)`

        styles[breakpointKey] = {
            display: visibilityBreakpoint.visible ? 'block' : 'none',
            minHeight:
                visibilityBreakpoint.visible && visibilityBreakpoint.placeholderHeight
                    ? visibilityBreakpoint.placeholderHeight
                    : undefined,
        }
    }

    return styles
}

// The following would be simpler to express as calcRem(...padding)
// however typescript does not currently correctly handle spreading
// tuples as function args.
// https://github.com/Microsoft/TypeScript/issues/4130
const getPadding = (padding: Padding) => padding.map(item => calcRem(item)).join(' ')

export interface BreakoutMargins {
    left: number | string
    right: number | string
}

export interface StyledAdUnitProps {
    visibilityBreakpoints?: VisiblityBreakpoint[]
    hidden?: boolean
    breakoutMargins?: BreakoutMargins
    showBackground?: boolean
    removeAdContainerStyles: boolean
}

export const StyledAdUnit = styled('div')<StyledAdUnitProps>(
    {
        display: 'block',
        margin: 0,
        fontSize: 0,
        textAlign: 'center',
    },
    ({ removeAdContainerStyles }) => {
        /**
         * DPO-217
         * If ad unit contains bonzai scrollx, dont apply the translate
         * hack used to center ad units. Also needs to be applied to the
         * StyledNotice component.
         */
        if (removeAdContainerStyles) {
            return
        }

        return {
            '@media (max-width: 360px)': {
                /**
                 * SWM-5397 & DPT-11
                 * Center ad unit inside grid item
                 */
                maxWidth: 254,
                position: 'relative',
                transform: 'translate(-50%)',
                left: '50%',
                margin: 0,
            },

            [`& .${gptAdSlotClassname} *`]: {
                display: 'block',
                margin: '0 auto',
            },

            '& div > *': {
                display: 'block',
                margin: '0 auto',

                '@media (max-width: 360px)': {
                    margin: 0,
                    transform: 'translate(-50%)',
                    left: '50%',
                    position: 'relative',
                },
            },
        }
    },
    ({ hidden, visibilityBreakpoints }) =>
        hidden ? { display: 'none' } : getBreakpointVisibilities(visibilityBreakpoints),
    ({ breakoutMargins }) => {
        if (!breakoutMargins) {
            return {}
        }

        return {
            marginLeft: breakoutMargins.left,
            marginRight: breakoutMargins.right,
        }
    },
    ({ showBackground }) => {
        if (!showBackground) {
            return {}
        }

        return {
            backgroundColor: colors.thewest.greyKoala,
        }
    }
)

StyledAdUnit.displayName = 'StyledAdUnit'

export interface StyledCenterProps {
    hidden?: boolean
    padding?: Padding
    visibilityBreakpoints: VisiblityBreakpoint[]
}

export const StyledCenter = styled('div')<StyledCenterProps>(
    ({ padding }) => ({
        textAlign: 'center',
        padding: padding !== undefined ? getPadding(padding) : undefined,
    }),
    ({ hidden, visibilityBreakpoints }) =>
        hidden ? { display: 'none' } : getBreakpointVisibilities(visibilityBreakpoints)
)

StyledCenter.displayName = 'StyledCenter'

export interface StyledNoticeProps {
    noticePosition: AdNoticePosition
    noticeMessage: string
    placeholderHeight?: number
    removeAdContainerStyles?: boolean
}

const getPseudoSelector = (position: AdNoticePosition) =>
    position !== 'below-right' ? '&::before' : '&::after'

export const StyledNotice = styled('div')<StyledNoticeProps>(
    {
        display: 'inline-block',
    },
    ({ removeAdContainerStyles, noticePosition, noticeMessage }) => {
        if (removeAdContainerStyles) {
            return {}
        }

        const noticePositionStyles: CSSObject =
            noticePosition === 'above-center'
                ? {
                      textAlign: 'center',
                      [getPseudoSelector(noticePosition)]: {
                          paddingBottom: calcRem(3),
                          textTransform: 'none',
                      },
                  }
                : { [getPseudoSelector(noticePosition)]: { textAlign: 'right' } }

        return {
            ...noticePositionStyles,

            '@media (max-width: 320px)': {
                display: 'block',
                transform: 'translate(-50%)',
                left: '50%',
                position: 'relative',
            },

            [getPseudoSelector(noticePosition)]: {
                display: 'block',
                marginTop: calcRem(6),
                width: '100%',
                fontSize: calcRem(10),
                color: colors.perthnow.greySlate,
                content: `'${noticeMessage}'`,
                textTransform: 'uppercase',
                fontFamily: fonts.perthnow.sansSerif,

                '@media (max-width: 320px)': {
                    textAlign: 'center',
                },
            },
        }
    }
)
StyledNotice.displayName = 'StyledNotice'

export const StyledStickyFooterAdUnit = styled('div')(({ theme }) => ({
    position: 'fixed',
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: themedValue(theme, {
        perthnow: zIndex.perthnow.stickyFooter,
        thewest: zIndex.thewest.stickyFooter,
        fallback: undefined,
    }),
    backgroundColor: colors.white,
    boxShadow: '0 0 5px 0 rgba(0, 0, 0, .2)',
}))

StyledStickyFooterAdUnit.displayName = 'StyledStickyFooter'

interface StyledAdUnitWrapperProps {
    stickyOffset?: number
}

export const StyledAdUnitWrapper = styled('div')<StyledAdUnitWrapperProps>(({ stickyOffset }) => ({
    ...conditionalStyles(typeof stickyOffset !== 'undefined', {
        position: 'sticky',
        top: `${stickyOffset}px`,
    }),
}))
StyledAdUnitWrapper.displayName = 'StyledAdUnitWrapper'
